import type { JSX } from 'react';

import type { PipelineStatus } from '../api/types';

/**
 * K4.2 (round-5) — Risk Heatmap strip above the PhaseRibbon.
 *
 * Derives a 4-cell risk tally from the 12 phase statuses: how many
 * phases are in each risk tier right now. This is the PM/PO's
 * single-glance "is anything on fire?" signal — if Risk 3 (failed or
 * cancelled) is non-zero the strip glows red and the rest of the
 * Command Center guides the operator into the offending phase.
 *
 * Phase-state → risk tier mapping, from phase-neg2 UX spec:
 *   Risk 0 (quiet)   : completed, pending (upstream of active)
 *   Risk 1 (watch)   : running
 *   Risk 2 (action)  : awaiting_approval
 *   Risk 3 (fault)   : failed, cancelled
 */
export function RiskHeatmap({ status }: { status: PipelineStatus }): JSX.Element {
  const tally = tallyPhases(status);
  const total = status.phases.length;
  const maxTier = (['r3', 'r2', 'r1'] as const).find((k) => tally[k] > 0);
  const headline = headlineFor(maxTier, tally);

  return (
    <section
      aria-label="Pipeline risk heatmap"
      className="shakti-card flex flex-col gap-3"
    >
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
          Risk heatmap
        </h2>
        <span className="text-xs text-fg-secondary">{headline}</span>
      </header>
      <div className="grid grid-cols-4 gap-2" role="group">
        <HeatCell tier={0} label="Quiet" count={tally.r0} total={total} />
        <HeatCell tier={1} label="Watch" count={tally.r1} total={total} />
        <HeatCell tier={2} label="Action" count={tally.r2} total={total} />
        <HeatCell tier={3} label="Fault" count={tally.r3} total={total} />
      </div>
    </section>
  );
}

function tallyPhases(status: PipelineStatus): {
  r0: number;
  r1: number;
  r2: number;
  r3: number;
} {
  const r = { r0: 0, r1: 0, r2: 0, r3: 0 };
  for (const p of status.phases) {
    switch (p.status) {
      case 'running':
        r.r1 += 1;
        break;
      case 'awaiting_approval':
        r.r2 += 1;
        break;
      case 'failed':
      case 'cancelled':
        r.r3 += 1;
        break;
      default:
        r.r0 += 1;
        break;
    }
  }
  return r;
}

function headlineFor(
  maxTier: 'r3' | 'r2' | 'r1' | undefined,
  tally: { r0: number; r1: number; r2: number; r3: number },
): string {
  if (maxTier === 'r3') return `${tally.r3} phase(s) failed or cancelled — action required`;
  if (maxTier === 'r2') return `${tally.r2} phase(s) awaiting approval`;
  if (maxTier === 'r1') return `${tally.r1} phase(s) running`;
  return 'All phases quiet';
}

function HeatCell({
  tier,
  label,
  count,
  total,
}: {
  tier: 0 | 1 | 2 | 3;
  label: string;
  count: number;
  total: number;
}): JSX.Element {
  const active = count > 0;
  // Use the same palette approvals uses, so the PM/PO internalises
  // a single colour language across the product.
  const toneActive = [
    'bg-status-success/15 text-status-success border-status-success/40',
    'bg-status-info/15 text-status-info border-status-info/40',
    'bg-status-warning/15 text-status-warning border-status-warning/40',
    'bg-status-error/15 text-status-error border-status-error/40',
  ][tier];
  const toneIdle = 'bg-bg-raised text-fg-muted border-border-subtle';
  const classes = `rounded-sm border px-3 py-3 ${active ? toneActive : toneIdle}`;
  return (
    <div className={classes} aria-label={`${label} risk tier: ${count} of ${total} phases`}>
      <div className="text-2xs font-semibold uppercase tracking-wider">
        Risk {tier} · {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{count}</div>
      <div className="text-2xs text-fg-muted">of {total}</div>
    </div>
  );
}
